import { useMutation, useQuery } from '@tanstack/react-query'
import {
    getUsersFromWorkspace,
    addUserToWorkspace,
    removeUserFromWorkspace,
    getWorkspaceData,
    updateWorkspaceWeekStartDay,
    getAccountMembers,
} from './accountApi'
import { getUserID } from '../../../utils'

export const useAccount = (selectedWorkspace, options = {}) => {
    const userID = getUserID()
    const { includeAccountMembers = false, accountID } = options

    // Query: Get account members (all users in the account)
    const {
        data: accountMembers,
        isLoading: loadingAccountMembers,
        refetch: refetchAccountMembers,
    } = useQuery({
        queryKey: ['accountMembers', accountID],
        queryFn: getAccountMembers,
        enabled: includeAccountMembers && !!accountID,
    })

    const {
        data: members,
        refetch,
        isLoading: loadingMembers,
    } = useQuery({
        queryKey: ['usersFromWorkspace', userID, selectedWorkspace],
        queryFn: () => getUsersFromWorkspace(selectedWorkspace),
        enabled: !!selectedWorkspace,
    })

    const { mutate: addUser, isPending: isAddingUser } = useMutation({
        mutationFn: ({ workspaceID, userID, role }) =>
            addUserToWorkspace(workspaceID, userID, role),
        onSuccess: () => refetch(),
    })

    const { mutate: changeRole, isPending: isChangingRole } = useMutation({
        mutationFn: ({ workspaceID, userID, role }) =>
            addUserToWorkspace(workspaceID, userID, role),
        onSuccess: (data, variables) => {
            variables?.onSuccess()
            refetch()
        },
    })

    const { mutate: removeUser, isPending: isRemovingUser } = useMutation({
        mutationFn: ({ workspaceID, userID }) => removeUserFromWorkspace(workspaceID, userID),
        onSuccess: (data, variables) => {
            variables?.onSuccess()
            refetch()
        },
    })

    const {
        data: workspaceData,
        refetch: refetchWorkspaceData,
        isLoading: isLoadingWorkspaceData,
        error: errorWorkspaceData,
    } = useQuery({
        queryKey: ['workspaceConfig', selectedWorkspace],
        queryFn: () => getWorkspaceData(selectedWorkspace),
    })

    const { mutate: updateWeekStartDay, isPending: isUpdatingWeekStartDay } = useMutation({
        mutationFn: ({ weekStartDay }) => updateWorkspaceWeekStartDay(workspaceData, weekStartDay),
        onSuccess: (data, variables) => {
            refetchWorkspaceData()
            variables?.onSuccess()
        },
    })

    return {
        // Account members
        accountMembers: accountMembers || [],
        loadingAccountMembers,
        refetchAccountMembers,

        // Workspace members
        members: members || [],
        loadingMembers,

        // Mutations
        addUser,
        isAddingUser,
        removeUser,
        isRemovingUser,
        changeRole,
        isChangingRole,

        // Workspace configuration
        workspaceData,
        refetchWorkspaceData,
        isLoadingWorkspaceData,
        errorWorkspaceData,
        updateWeekStartDay,
        isUpdatingWeekStartDay,
    }
}
